"use client";

import { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

// 1. Props için Interface tanımlıyoruz
export interface DialogProps {
    message: string | null;
    type?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    onClose?: () => void;
    isVisible?: boolean;
    title?: string | null;
}

const Dialog = ({
    message,
    type = 'info',
    duration = 5000,
    onClose,
    isVisible = false,
    title = null
}: DialogProps) => {
    const [visible, setVisible] = useState<boolean>(isVisible);

    useEffect(() => {
        setVisible(isVisible);

        if (isVisible && duration > 0) {
            const timer = setTimeout(() => {
                handleClose();
            }, duration);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, duration]);

    const handleClose = () => {
        setVisible(false);
        setTimeout(() => {
            onClose?.();
        }, 300);
    };

    // 2. React MouseEvent tipini ekliyoruz
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle className="w-8 h-8" />;
            case 'error':
                return <AlertCircle className="w-8 h-8" />;
            case 'warning':
                return <AlertTriangle className="w-8 h-8" />;
            default:
                return <Info className="w-8 h-8" />;
        }
    };

    const getStyles = () => {
        const baseStyles = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 transition-opacity duration-300";
        const visibilityStyles = visible ? "opacity-100" : "opacity-0 pointer-events-none";
        return `${baseStyles} ${visibilityStyles}`;
    };

    const getModalStyles = () => {
        const baseStyles = "bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-auto transform transition-all duration-300";
        const visibilityStyles = visible ? "scale-100 opacity-100" : "scale-95 opacity-0";
        return `${baseStyles} ${visibilityStyles}`;
    };

    const getIconStyles = () => {
        // Record<string, string> ile TypeScript'e objenin yapısını belirtiyoruz
        const styles: Record<NonNullable<DialogProps['type']>, string> = {
            success: "text-green-500 bg-green-50",
            error: "text-red-500 bg-red-50",
            warning: "text-yellow-500 bg-yellow-50",
            info: "text-blue-500 bg-blue-50"
        };
        return styles[type];
    };

    const getTitle = () => {
        if (title) return title;

        switch (type) {
            case 'success':
                return 'Başarılı';
            case 'error':
                return 'Hata';
            case 'warning':
                return 'Uyarı';
            default:
                return 'Bilgi';
        }
    };

    // message yoksa hiç render etme (Güvenlik)
    if (!message) return null;
    if (!isVisible && !visible) return null;

    return (
        <div className={getStyles()} onClick={handleBackdropClick}>
            <div className={getModalStyles()}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-3 ${getIconStyles()}`}>
                            {getIcon()}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {getTitle()}
                            </h3>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="shrink-0 inline-flex text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Message */}
                <div className="mb-6">
                    <p className="text-gray-600 text-center">
                        {message}
                    </p>
                </div>

                {/* Action Button */}
                <div className="flex justify-center">
                    <button
                        onClick={handleClose}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Tamam
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Dialog;